// Budget lines and the year grid. Mirrors the DTOs in internal/finance.
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import type { Budget, ExpenseTag } from "./meta";

export type LineKind = "income" | "expense" | "formula";
export type CellStatus = "pending" | "done";

/** One month of one line. `null` in a cells array means the cell is untouched. */
export interface Cell {
  amount: number | null;
  status: CellStatus;
  note: string | null;
}

export interface BudgetLine {
  id: string;
  kind: LineKind;
  name: string;
  tagId: string | null;
  planned: number | null;
  formulaText: string | null;
  position: number;
  /**
   * Month number ("1".."12") to amount, for months that deviate from `planned`.
   * A key present with null means "explicitly nothing that month"; a missing
   * key simply uses the default.
   */
  planOverrides: Record<string, number | null> | null;
}

export interface GridLine {
  id: string;
  kind: LineKind;
  name: string;
  tagId: string | null;
  planned: number | null;
  /** The plan resolved for each month: the override where set, else the default. */
  plans: (number | null)[];
  formulaText: string | null;
  cells: (Cell | null)[];
  values: number[] | null; // formula lines only
  error: string | null;
  yearTotal: number;
}

export interface GridSection {
  kind: LineKind;
  label: string;
  tag: ExpenseTag | null;
  lines: GridLine[];
  subtotals: number[];
}

export interface Grid {
  year: number;
  budget: Budget;
  sections: GridSection[];
  totals: { income: number[]; expenses: number[]; balance: number[] };
}

const CURRENT_BUDGET_KEY = "familylife.budgetId";

/**
 * The budget being worked on, shared by the Overview and Configuration and
 * remembered across reloads.
 *
 * Everything below a budget — its lines, and therefore every cell — belongs to
 * that budget alone. Holding the selection in one place keeps the two screens
 * from disagreeing about which budget you are editing.
 */
export function useCurrentBudget(budgets: Budget[]): [string | null, (id: string) => void] {
  const [stored, setStored] = useState<string | null>(() => {
    try {
      return localStorage.getItem(CURRENT_BUDGET_KEY);
    } catch {
      return null;
    }
  });

  // Fall back to the first budget if the remembered one is gone.
  const current = budgets.some((b) => b.id === stored) ? stored : (budgets[0]?.id ?? null);

  const select = useCallback((id: string) => {
    setStored(id);
    try {
      localStorage.setItem(CURRENT_BUDGET_KEY, id);
    } catch {
      // Private browsing and the like: the selection just will not persist.
    }
  }, []);

  return [current, select];
}

export interface NewLine {
  kind: LineKind;
  name: string;
  tagId?: string | null;
  planned?: number | null;
  formula?: string;
}

/** The lines of one budget, for the budget page. */
export function useBudgetLines(budgetId: string | null) {
  const [lines, setLines] = useState<BudgetLine[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!budgetId) {
      setLines([]);
      setLoading(false);
      return;
    }
    try {
      const data = await api.get<BudgetLine[]>(`/api/finance/budgets/${budgetId}/lines`);
      setLines(data ?? []);
    } finally {
      setLoading(false);
    }
  }, [budgetId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const addLine = useCallback(async (line: NewLine) => {
    await api.post(`/api/finance/budgets/${budgetId}/lines`, line);
    await reload();
  }, [budgetId, reload]);

  const updateLine = useCallback(async (id: string, patch: Partial<NewLine>) => {
    await api.patch(`/api/finance/lines/${id}`, patch);
    await reload();
  }, [reload]);

  const deleteLine = useCallback(async (id: string) => {
    await api.del(`/api/finance/lines/${id}`);
    await reload();
  }, [reload]);

  const reorder = useCallback(async (ids: string[]) => {
    await api.post(`/api/finance/budgets/${budgetId}/lines/reorder`, { ids });
    await reload();
  }, [budgetId, reload]);

  /** Override one month's plan. `month` is 1-based; null means "nothing that month". */
  const setPlan = useCallback(async (id: string, month: number, amount: number | null) => {
    await api.put(`/api/finance/lines/${id}/plan`, { month, amount });
    await reload();
  }, [reload]);

  /** Drop one month's override, returning it to the line default. */
  const clearPlan = useCallback(async (id: string, month: number) => {
    await api.del(`/api/finance/lines/${id}/plan?month=${month}`);
    await reload();
  }, [reload]);

  return { lines, loading, addLine, updateLine, deleteLine, reorder, setPlan, clearPlan, reload };
}

/**
 * The year grid. Cell writes answer with the recalculated grid, so subtotals and
 * totals stay right without a refetch.
 */
export function useGrid(budgetId: string | null, year: number) {
  const [grid, setGrid] = useState<Grid | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!budgetId) {
      setGrid(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setGrid(await api.get<Grid>(`/api/finance/budgets/${budgetId}/grid?year=${year}`));
    } finally {
      setLoading(false);
    }
  }, [budgetId, year]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Write a cell. `month` is 1-based. */
  const setCell = useCallback(
    async (lineId: string, month: number, amount: number | null, status: CellStatus = "done") => {
      setGrid(await api.put<Grid>("/api/finance/entries", { lineId, year, month, amount, status }));
    },
    [year],
  );

  /** Clear a cell back to pending. */
  const clearCell = useCallback(async (lineId: string, month: number) => {
    setGrid(await api.del<Grid>(`/api/finance/entries?lineId=${lineId}&year=${year}&month=${month}`));
  }, [year]);

  return { grid, loading, setCell, clearCell, reload };
}
